import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import SiteHeader from '../components/wallet/SiteHeader';
import PageFooter from '../components/wallet/PageFooter';
import ErrorState from '../components/wallet/ErrorState';
import Add2WalletLogo from '../components/wallet/Add2WalletLogo';

// JIT wallet lite-links (Phase 2, 2026-08-28) — the /link/:token landing
// page. Distinct from WalletDisplay: that page resolves an ALREADY-MINTED
// pass; this page resolves a lite-link TOKEN, which may still need the
// customer to fill in a gap (usually mobile) before a pass exists at all.
// On successful mint it hands off into the normal WalletDisplay flow via a
// full navigation to /{security_key}/{keyword} — no duplicated Apple/
// Google/Samsung button logic here.
//
// KNOWN GAP: no server-side rate limiting / bot protection yet on the mint
// call (docs/TODO.md #19) — this page is otherwise fully public/anonymous.

const PHONE_PATTERN = /^\+?[0-9 ()-]{8,20}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type ResolveState =
  | { status: 'loading' }
  | { status: 'error'; title: string; message: string; canRetry: boolean }
  | { status: 'minted'; urlPath: string }
  | {
      status: 'pending';
      merchantId: string;
      passTemplateId: string;
      prefill: { mobile: string | null; email: string | null; merchant_customer_id: string | null };
      lockedFields: Record<string, unknown>;
      missing: string[];
    };

const LiteLinkLanding: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [apiBase, setApiBase] = useState<string>('');
  const [state, setState] = useState<ResolveState>({ status: 'loading' });
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ mobile?: string; email?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/amplify_outputs.json')
      .then((r) => r.json())
      .then((config) => {
        if (cancelled) return;
        const endpoint = config?.custom?.API?.WalletHttpApi?.endpoint;
        setApiBase(typeof endpoint === 'string' ? endpoint.replace(/\/$/, '') : 'https://t7d9a6p352.execute-api.ap-southeast-2.amazonaws.com/prod');
      })
      .catch(() => {
        if (!cancelled) setApiBase('https://t7d9a6p352.execute-api.ap-southeast-2.amazonaws.com/prod');
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!apiBase || !token) return;
    let cancelled = false;
    fetch(`${apiBase}/link/${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (cancelled) return;
        if (r.status === 404) {
          setState({ status: 'error', title: 'Link not found', message: 'This link is invalid or has expired.', canRetry: false });
          return;
        }
        if (!r.ok) {
          setState({ status: 'error', title: 'Something went wrong', message: 'We could not load this link right now.', canRetry: true });
          return;
        }
        const data = await r.json();
        if (data.status === 'minted') {
          setState({ status: 'minted', urlPath: data.url_path });
          return;
        }
        setState({
          status: 'pending',
          merchantId: data.merchant_id,
          passTemplateId: data.pass_template_id,
          prefill: data.prefill,
          lockedFields: data.locked_fields || {},
          missing: data.missing || [],
        });
        setMobile(data.prefill?.mobile || '');
        setEmail(data.prefill?.email || '');
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error', title: 'Something went wrong', message: 'We could not load this link right now.', canRetry: true });
      });
    return () => { cancelled = true; };
  }, [apiBase, token]);

  // Already-minted tokens skip the form entirely — straight into the real
  // pass flow, same as a second click on an already-added wallet keyword.
  useEffect(() => {
    if (state.status === 'minted') {
      window.location.replace(`/${state.urlPath}`);
    }
  }, [state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (state.status !== 'pending') return;

    const errors: { mobile?: string; email?: string } = {};
    const needsMobile = state.missing.includes('mobile');
    const needsEmail = state.missing.includes('email');
    if (needsMobile && !PHONE_PATTERN.test(mobile.trim())) errors.mobile = 'Enter a valid mobile number.';
    if (needsEmail && !EMAIL_PATTERN.test(email.trim())) errors.email = 'Enter a valid email address.';
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(`${apiBase}/link/${encodeURIComponent(token || '')}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(needsMobile ? { mobile: mobile.trim() } : {}),
          ...(needsEmail ? { email: email.trim() } : {}),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setSubmitError(body?.error || 'Something went wrong — please try again.');
        setSubmitting(false);
        return;
      }
      const data = await res.json();
      window.location.replace(`/${data.url_path}`);
    } catch {
      setSubmitError('Something went wrong — please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SiteHeader />
      <main className="flex-1">
        {state.status === 'loading' && (
          <div className="max-w-md mx-auto px-4 py-16 text-center text-gray-500 text-sm">Loading…</div>
        )}
        {state.status === 'error' && (
          <ErrorState title={state.title} message={state.message} canRetry={state.canRetry} onRetry={() => window.location.reload()} />
        )}
        {state.status === 'minted' && (
          <div className="max-w-md mx-auto px-4 py-16 text-center text-gray-500 text-sm">Redirecting…</div>
        )}
        {state.status === 'pending' && (
          <div className="max-w-md mx-auto px-4 py-12">
            <h1 className="text-xl font-bold text-gray-900 mb-1">Just a couple of details</h1>
            <p className="text-sm text-gray-600 mb-6">We just need this to finish setting up your pass.</p>

            {Object.keys(state.lockedFields).length > 0 && (
              <div className="mb-6 rounded-xl border border-gray-100 bg-gray-50 p-4">
                {Object.entries(state.lockedFields).map(([k, v]) => (
                  <div key={k} className="flex justify-between text-sm py-1">
                    <span className="text-gray-500 capitalize">{k.replace(/_/g, ' ')}</span>
                    <span className="text-gray-900 font-medium">{String(v)}</span>
                  </div>
                ))}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {state.missing.includes('mobile') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mobile number</label>
                  <input
                    type="tel"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 h-11 text-sm focus:outline-none focus:ring-2 focus:ring-[#AEDD1F]"
                    placeholder="+61 4XX XXX XXX"
                  />
                  {fieldErrors.mobile && <p className="text-xs text-red-500 mt-1">{fieldErrors.mobile}</p>}
                </div>
              )}
              {state.missing.includes('email') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 h-11 text-sm focus:outline-none focus:ring-2 focus:ring-[#AEDD1F]"
                    placeholder="you@example.com"
                  />
                  {fieldErrors.email && <p className="text-xs text-red-500 mt-1">{fieldErrors.email}</p>}
                </div>
              )}
              {submitError && <p className="text-sm text-red-500">{submitError}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center h-11 rounded-full bg-[#AEDD1F] text-[#06251B] text-sm font-semibold hover:bg-[#9CCB1B] transition disabled:opacity-60"
              >
                {submitting ? 'Setting up your pass…' : 'Join now'}
              </button>
            </form>

            <div className="mt-10 flex justify-center opacity-60">
              <Add2WalletLogo size={20} />
            </div>
          </div>
        )}
      </main>
      <PageFooter />
    </div>
  );
};

export default LiteLinkLanding;
